<!-- Header -->
<div class="mb-6">
  <h2 class="text-xl font-bold text-gray-900">Reports</h2>
</div>

<!-- Filters -->
<div class="bg-white rounded-xl border border-gray-100 shadow-sm p-5 mb-4">
  <h3 class="text-sm font-semibold text-gray-700 mb-4">Report Options</h3>
  <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Report Type</label>
      @if (isAdmin()) {
        <select [(ngModel)]="selectedReportType"
                class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none">
          <option value="financial">Financial Report</option>
          <option value="payment-history">Payment History</option>
          <option value="members">Member Report</option>
        </select>
      } @else {
        <div class="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-700">
          My Payment History
        </div>
      }
    </div>
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
      <input type="date" [(ngModel)]="startDate"
             class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"/>
    </div>
    <div>
      <label class="block text-sm font-medium text-gray-700 mb-1">End Date</label>
      <input type="date" [(ngModel)]="endDate"
             class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"/>
    </div>
    @if (isAdmin() && (selectedReportType === 'payment-history' || selectedReportType === 'members')) {
      <div>
        <label class="block text-sm font-medium text-gray-700 mb-1">Member ID (Optional)</label>
        <input type="text" [(ngModel)]="memberId" placeholder="Leave empty for all members"
               class="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none"/>
      </div>
    }
  </div>
  <div class="flex flex-wrap gap-3">
    <button (click)="generateReport()" [disabled]="isLoading"
            class="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
      @if (isLoading) {
        <app-spinner size="xs" colour="white"></app-spinner>
        Generating...
      } @else {
        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
        </svg>
        Generate Report
      }
    </button>
    @if (reportData) {
      <button (click)="exportReport('pdf')" [disabled]="isLoading"
              class="flex items-center gap-2 bg-red-600 hover:bg-red-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
        Export PDF
      </button>
      <button (click)="exportReport('excel')" [disabled]="isLoading"
              class="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-medium px-4 py-2 rounded-lg transition-colors">
        Export Excel
      </button>
    }
  </div>
</div>

<!-- Error -->
@if (errorMessage) {
  <div class="flex items-center gap-2 bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
    <svg class="w-5 h-5 text-red-500 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
    </svg>
    <p class="text-sm text-red-700 flex-1">{{ errorMessage }}</p>
    <button (click)="errorMessage = ''" class="text-red-400 hover:text-red-600">
      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>
  </div>
}

<!-- Loading -->
@if (isLoading) {
  <app-loader caption="Generating report…"></app-loader>
}

<!-- Results -->
@if (reportData && !isLoading) {
  <div class="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
    <div class="px-5 py-4 border-b border-gray-100">
      <h3 class="text-sm font-semibold text-gray-700">
        {{ selectedReportType === 'financial' ? 'Financial Report' : selectedReportType === 'payment-history' ? 'Payment History' : 'Member Report' }}
      </h3>
    </div>
    <div class="p-5">

      <!-- Financial -->
      @if (selectedReportType === 'financial') {
        <div class="grid grid-cols-1 md:grid-cols-3 gap-4 mb-5">
          <div class="bg-green-50 border border-green-100 rounded-xl p-5 text-center">
            <p class="text-sm text-green-600 font-medium mb-1">Total Revenue</p>
            <p class="text-2xl font-bold text-green-700">Rs. {{ (reportData.totalRevenue || 0) | number:'1.2-2' }}</p>
          </div>
          <div class="bg-red-50 border border-red-100 rounded-xl p-5 text-center">
            <p class="text-sm text-red-600 font-medium mb-1">Total Expenses</p>
            <p class="text-2xl font-bold text-red-700">Rs. {{ (reportData.totalExpenses || 0) | number:'1.2-2' }}</p>
          </div>
          <div class="bg-blue-50 border border-blue-100 rounded-xl p-5 text-center">
            <p class="text-sm text-blue-600 font-medium mb-1">Net Balance</p>
            <p class="text-2xl font-bold text-blue-700">Rs. {{ (reportData.netBalance || 0) | number:'1.2-2' }}</p>
          </div>
        </div>
        <p class="text-sm text-gray-500">
          Period: {{ reportData.periodStart | date:'shortDate' }} to {{ reportData.periodEnd | date:'shortDate' }}
        </p>
      }

      <!-- Payment History -->
      @if (selectedReportType === 'payment-history') {
        <div class="mb-4">
          <p class="text-lg font-bold text-gray-900 mb-1">Rs. {{ (reportData.totalAmount || 0) | number:'1.2-2' }}</p>
          @if (reportData.payments) {
            <p class="text-sm text-gray-500">{{ reportData.payments.length }} payments total</p>
          }
        </div>
        @if (reportData.payments && reportData.payments.length > 0) {
          <!-- Mobile card list -->
          <div class="md:hidden divide-y divide-gray-100">
            @for (payment of reportData.payments; track payment) {
              <div class="p-4 flex items-start justify-between gap-3">
                <div class="min-w-0 flex-1">
                  <div class="text-gray-900 font-medium truncate">
                    @if (isAdmin()) { {{ payment.memberName || '-' }} } @else { {{ payment.receiptNo || payment.ReceiptNo || '-' }} }
                  </div>
                  <div class="text-xs text-gray-500 mt-0.5">
                    {{ payment.paidDate || payment.date || payment.createdAt | date:'shortDate' }}
                    · {{ payment.paymentMethod || payment.PaymentMethod || '-' }}
                  </div>
                  <div class="text-xs text-gray-500 mt-0.5">
                    {{ payment.businessUnitName || '-' }}
                    @if (isAdmin()) { · <span class="font-mono">{{ payment.receiptNo || payment.ReceiptNo || '-' }}</span> }
                  </div>
                </div>
                <div class="font-medium text-gray-900 whitespace-nowrap">Rs. {{ (payment.amount || payment.Amount || 0) | number:'1.2-2' }}</div>
              </div>
            }
          </div>

          <!-- Desktop table -->
          <div class="hidden md:block overflow-x-auto">
            <table class="w-full text-sm">
              <thead class="bg-gray-50 border-b border-gray-100">
                <tr>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Receipt No</th>
                  @if (isAdmin()) {
                    <th class="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Member</th>
                  }
                  <th class="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Committee</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                  <th class="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider">Method</th>
                </tr>
              </thead>
              <tbody class="divide-y divide-gray-200">
                @for (payment of reportData.payments; track payment) {
                  <tr class="odd:bg-white even:bg-gray-100 hover:bg-blue-100 transition-colors">
                    <td class="px-4 py-3 text-gray-600">{{ payment.paidDate || payment.date || payment.createdAt | date:'shortDate' }}</td>
                    <td class="px-4 py-3 text-gray-600">{{ payment.receiptNo || payment.ReceiptNo || '-' }}</td>
                    @if (isAdmin()) {
                      <td class="px-4 py-3 text-gray-600">{{ payment.memberName || '-' }}</td>
                    }
                    <td class="px-4 py-3 text-gray-600">{{ payment.businessUnitName || '-' }}</td>
                    <td class="px-4 py-3 text-gray-600">Rs. {{ (payment.amount || payment.Amount || 0) | number:'1.2-2' }}</td>
                    <td class="px-4 py-3 text-gray-600">{{ payment.paymentMethod || payment.PaymentMethod || '-' }}</td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        }
      }

      <!-- Member Report -->
      @if (selectedReportType === 'members') {
        <p class="text-sm text-gray-500 mb-3">Member report data</p>
        <pre class="bg-gray-50 rounded-lg p-4 text-xs text-gray-700 overflow-x-auto">{{ reportData | json }}</pre>
      }

    </div>
  </div>
}
